import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';
import AdminNotificationsBell from './AdminNotificationsBell';
import { Menu, X, User, LogOut, Plus, ShoppingCart, ShieldCheck, Package, Film, MessageCircle, Heart } from 'lucide-react';

export default function Header() {
  const { user, isAuthenticated, logout, cartCount, ordersCount, messagesCount } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleSellClick = () => {
    if (!isAuthenticated) { setShowAuth(true); return; }
    if (!user?.profile_completed) { navigate('/complete-profile'); return; }
    navigate('/sell');
  };

  const isVerifiedInfluencer = user?.profile?.is_influencer && user?.profile?.influencer_verification_status === 'approved';

  return (
    <>
      <header className="backdrop-blur-xl bg-white/80 border-b border-cream-100/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center shrink-0 gap-2" data-testid="header-logo">
              <img src="/logo-horizontal.png" alt="{Beauty} Stashmart" className="h-7 md:h-9 w-auto select-none" draggable={false} />
              <span className="bg-[#F4C2C2] text-[#2C2A29] text-[8px] font-body font-bold px-1.5 py-0.5 rounded-full" data-testid="header-beta-badge">BETA</span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-5">
              <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="font-body text-sm font-medium text-[#78716C] hover:text-[#2C2A29] transition-colors" data-testid="nav-home">Home</Link>
              <button onClick={() => { const el = document.getElementById('from-creators'); if (el) { const y = el.getBoundingClientRect().top + window.scrollY - 80; window.scrollTo({ top: y, behavior: 'smooth' }); } else { navigate('/'); setTimeout(() => { const target = document.getElementById('from-creators'); if (target) { const ty = target.getBoundingClientRect().top + window.scrollY - 80; window.scrollTo({ top: ty, behavior: 'smooth' }); } }, 500); } }} className="font-body text-sm font-medium text-[#78716C] hover:text-[#2C2A29] transition-colors" data-testid="nav-from-creators">From Creators</button>
              {!user?.is_admin && (
                <button onClick={handleSellClick}
                  className="bg-[#C5A880] text-white rounded-full px-4 py-2 font-body text-sm font-semibold transition-all hover:bg-[#B89970] hover:shadow-md flex items-center gap-1.5"
                  data-testid="nav-sell">
                  <Plus size={14} /> Start Selling
                </button>
              )}

              {isVerifiedInfluencer && !user?.is_admin && (
                <button onClick={() => navigate('/post-video')}
                  className="bg-[#FFF5F5] text-[#D48585] rounded-full px-4 py-2 font-body text-sm font-semibold transition-all hover:bg-[#ECAEAE] hover:text-white hover:shadow-md flex items-center gap-1.5 border border-[#F4C2C2]/30"
                  data-testid="nav-post-content">
                  <Film size={14} /> Post Content
                </button>
              )}

              {isAuthenticated && !user?.is_admin && (
                <Link to="/cart" className="relative text-[#78716C] hover:text-[#2C2A29] transition-colors" data-testid="nav-cart">
                  <ShoppingCart size={20} />
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-[#F4C2C2] text-[#2C2A29] text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid="cart-badge">
                      {cartCount}
                    </span>
                  )}
                </Link>
              )}

              {isAuthenticated && !user?.is_admin && (
                <Link to="/wishlist" className="text-[#78716C] hover:text-[#D48585] transition-colors" data-testid="nav-wishlist">
                  <Heart size={20} />
                </Link>
              )}

              {isAuthenticated && (
                <Link to="/messages" className="relative text-[#78716C] hover:text-[#2C2A29] transition-colors" data-testid="nav-messages">
                  <MessageCircle size={20} />
                  {messagesCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-[#D48585] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid="messages-badge">
                      {messagesCount > 9 ? '9+' : messagesCount}
                    </span>
                  )}
                </Link>
              )}

              {isAuthenticated && !user?.is_admin && (
                <Link to="/orders" className="relative text-[#78716C] hover:text-[#2C2A29] transition-colors" data-testid="nav-orders">
                  <Package size={20} />
                  {ordersCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-[#C5A880] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid="orders-badge">
                      {ordersCount}
                    </span>
                  )}
                </Link>
              )}

              {isAuthenticated ? (
                <div className="flex items-center gap-4">
                  <Link to={`/profile/${user?.user_id}`}
                    className="flex items-center gap-2 font-body text-sm font-medium text-[#78716C] hover:text-[#2C2A29] transition-colors"
                    data-testid="nav-profile">
                    {user?.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover border-2 border-blush-200" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-blush-200 flex items-center justify-center"><User size={14} className="text-[#2C2A29]" /></div>
                    )}
                    <span className="hidden lg:inline">{user?.name?.split(' ')[0]}</span>
                  </Link>
                  <button onClick={async () => { await logout(); navigate('/', { replace: true }); }} className="text-[#78716C] hover:text-[#2C2A29] transition-colors" data-testid="nav-logout" title="Logout"><LogOut size={18} /></button>
                  {user?.is_admin && (
                    <>
                      <AdminNotificationsBell />
                      <Link to="/admin" className="text-[#C5A880] hover:text-[#B89970] transition-colors" title="Admin" data-testid="nav-admin"><ShieldCheck size={18} /></Link>
                    </>
                  )}
                </div>
              ) : (
                <button onClick={() => setShowAuth(true)}
                  className="bg-[#F4C2C2] text-[#2C2A29] rounded-full px-5 py-2 font-body text-sm font-semibold transition-all hover:bg-[#ECAEAE] hover:shadow-md"
                  data-testid="nav-signin">Sign In</button>
              )}
            </nav>

            {/* Mobile right side */}
            <div className="flex md:hidden items-center gap-3">
              {isAuthenticated && !user?.is_admin && (
                <Link to="/cart" className="relative text-[#78716C]" data-testid="mobile-cart">
                  <ShoppingCart size={20} />
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-[#F4C2C2] text-[#2C2A29] text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{cartCount}</span>
                  )}
                </Link>
              )}
              {isAuthenticated && !user?.is_admin && (
                <Link to="/wishlist" className="text-[#78716C]" data-testid="mobile-wishlist">
                  <Heart size={18} />
                </Link>
              )}
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-[#2C2A29]" data-testid="mobile-menu-toggle">
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Nav */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 border-t border-cream-100/50 mt-2 pt-4 space-y-3">
              <Link to="/" onClick={() => { setMobileMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="block text-center font-body text-sm font-medium text-[#78716C] py-2" data-testid="mobile-nav-home">Home</Link>
              <button onClick={() => { setMobileMenuOpen(false); setTimeout(() => { const el = document.getElementById('from-creators'); if (el) { const y = el.getBoundingClientRect().top + window.scrollY - 80; window.scrollTo({ top: y, behavior: 'smooth' }); } else { navigate('/'); setTimeout(() => { const target = document.getElementById('from-creators'); if (target) { const ty = target.getBoundingClientRect().top + window.scrollY - 80; window.scrollTo({ top: ty, behavior: 'smooth' }); } }, 500); } }, 350); }} className="block w-full text-center font-body text-sm font-medium text-[#78716C] py-2" data-testid="mobile-nav-from-creators">From Creators</button>
              {!user?.is_admin && (
                <button onClick={() => { setMobileMenuOpen(false); handleSellClick(); }}
                  className="block w-full text-center bg-[#C5A880] text-white rounded-xl px-4 py-2.5 font-body text-sm font-semibold"
                  data-testid="mobile-nav-sell">
                  Start Selling
                </button>
              )}
              {isVerifiedInfluencer && !user?.is_admin && (
                <Link to="/post-video" onClick={() => setMobileMenuOpen(false)}
                  className="block w-full text-center bg-[#FFF5F5] text-[#D48585] rounded-xl px-4 py-2.5 font-body text-sm font-semibold border border-[#F4C2C2]/30"
                  data-testid="mobile-nav-post-content">
                  Post Content
                </Link>
              )}
              {isAuthenticated ? (
                <>
                  <Link to="/messages" onClick={() => setMobileMenuOpen(false)} className="block text-center font-body text-sm font-medium text-[#78716C] py-2 relative" data-testid="mobile-nav-messages">
                    Messages
                    {messagesCount > 0 && (
                      <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] bg-[#D48585] text-white text-[10px] font-bold rounded-full px-1">{messagesCount > 9 ? '9+' : messagesCount}</span>
                    )}
                  </Link>
                  {!user?.is_admin && (
                    <Link to="/orders" onClick={() => setMobileMenuOpen(false)} className="block text-center font-body text-sm font-medium text-[#78716C] py-2" data-testid="mobile-nav-orders">
                      My Orders
                    </Link>
                  )}
                  {user?.is_admin && (
                    <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="block text-center font-body text-sm font-medium text-[#C5A880] py-2" data-testid="mobile-nav-admin">
                      Admin Panel
                    </Link>
                  )}
                  <Link to={`/profile/${user?.user_id}`} onClick={() => setMobileMenuOpen(false)} className="block text-center font-body text-sm font-medium text-[#78716C] py-2" data-testid="mobile-nav-profile">
                    My Profile
                  </Link>
                  <button onClick={async () => { setMobileMenuOpen(false); await logout(); navigate('/', { replace: true }); }} className="block w-full text-center font-body text-sm font-medium text-[#78716C] py-2" data-testid="mobile-nav-logout">
                    Logout
                  </button>
                </>
              ) : (
                <button onClick={() => { setMobileMenuOpen(false); setShowAuth(true); }}
                  className="w-full bg-[#F4C2C2] text-[#2C2A29] rounded-full px-5 py-2.5 font-body text-sm font-semibold"
                  data-testid="mobile-nav-signin">Sign In</button>
              )}
            </div>
          )}
        </div>
      </header>
      <AuthModal open={showAuth} onOpenChange={setShowAuth} />
    </>
  );
}
